import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Package, 
  FileText, 
  Coins, 
  Calendar, 
  BarChart3, 
  Settings,
  ChevronDown,
  Building2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCompanyInfo } from '@/context/CompanyContext';
import { cn } from '@/utils/cn';

interface SidebarProps {
  isCollapsed: boolean;
  onNavigate?: () => void;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  permission: string | string[];
  children?: NavItem[];
}

const navigation: NavItem[] = [
  {
    name: 'Dashboard',
    href: '/',
    icon: Home,
    permission: [],
  },
  {
    name: 'Clientes',
    href: '/customers',
    icon: Users,
    permission: ['view_customers', 'manage_customers'],
  },
  {
    name: 'Productos y Servicios',
    href: '/items',
    icon: Package,
    permission: ['view_items', 'manage_items'],
  },
  {
    name: 'Cotizaciones',
    href: '/quotations',
    icon: FileText,
    permission: ['view_quotations', 'manage_quotations'],
  },
  {
    name: 'Ventas',
    href: '/sales',
    icon: Coins,
    permission: ['view_sales', 'manage_sales'],
  },
  {
    name: 'Citas',
    href: '/appointments',
    icon: Calendar,
    permission: ['manage_appointments'],
  },
  {
    name: 'Reportes',
    href: '/reports',
    icon: BarChart3,
    permission: ['view_reports', 'view_financial_reports'],
  },
  {
    name: 'Configuración',
    href: '/settings',
    icon: Settings,
    permission: ['manage_companies'],  // Only superusers can access settings
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onNavigate }) => {
  const location = useLocation();
  const { hasPermission, user } = useAuth();
  const { displayName, logoUrl, logoShape, logoZoom } = useCompanyInfo();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  const toggleExpanded = (name: string) => {
    setExpandedItems(prev => 
      prev.includes(name) 
        ? prev.filter(item => item !== name)
        : [...prev, name]
    );
  };

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const canAccess = (permission: string | string[]) => {
    if (Array.isArray(permission) && permission.length === 0) return true;
    return hasPermission(permission);
  };

  return (
    <div className={cn(
      'bg-white shadow-sm border-r border-gray-200 h-full flex flex-col transition-all duration-300',
      isCollapsed ? 'w-16' : 'w-64'
    )}>
      {/* Logo */}
      <div className="flex items-center justify-center h-16 px-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          {logoUrl ? (
            <div className={`h-8 w-8 overflow-hidden flex items-center justify-center ${logoShape === 'circle' ? 'rounded-full' : 'rounded'}`}>
              <img
                src={logoUrl}
                alt="Logo"
                className="w-full h-full object-cover"
                style={{ transform: `scale(${logoZoom / 100})` }}
              />
            </div>
          ) : (
            <Building2 className="h-8 w-8 text-primary-600" />
          )}
          {!isCollapsed && (
            <span className="text-xl font-bold text-gray-900">{displayName}</span>
          )}
        </div>
      </div>

      {/* User Info */}
      {!isCollapsed && user && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                </span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user.role.name}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <div className="space-y-1 px-2">
          {navigation.map((item) => {
            if (!canAccess(item.permission)) return null;

            const isItemActive = isActive(item.href);
            const isExpanded = expandedItems.includes(item.name);
            const hasChildren = item.children && item.children.length > 0;

            return (
              <div key={item.name}>
                {hasChildren ? (
                  <button
                    onClick={() => toggleExpanded(item.name)}
                    className={cn(
                      'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      isItemActive
                        ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    )}
                  >
                    <item.icon className={cn('flex-shrink-0 h-5 w-5', !isCollapsed && 'mr-3')} />
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 text-left">{item.name}</span>
                        <ChevronDown className={cn(
                          'h-4 w-4 transition-transform',
                          isExpanded && 'transform rotate-180'
                        )} />
                      </>
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    onClick={onNavigate}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      isItemActive
                        ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    )}
                    title={isCollapsed ? item.name : undefined}
                  >
                    <item.icon className={cn('flex-shrink-0 h-5 w-5', !isCollapsed && 'mr-3')} />
                    {!isCollapsed && <span>{item.name}</span>}
                  </Link>
                )}

                {/* Children */}
                {hasChildren && isExpanded && !isCollapsed && (
                  <div className="ml-8 mt-1 space-y-1">
                    {item.children?.map((child) => {
                      if (!canAccess(child.permission)) return null;
                      
                      const isChildActive = isActive(child.href);
                      
                      return (
                        <Link
                          key={child.name}
                          to={child.href}
                          onClick={onNavigate}
                          className={cn(
                            'flex items-center px-3 py-2 text-sm rounded-md transition-colors',
                            isChildActive
                              ? 'bg-primary-50 text-primary-700'
                              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                          )}
                        >
                          <child.icon className="flex-shrink-0 h-4 w-4 mr-2" />
                          <span>{child.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>
    </div>
  );
};